import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import BlogCard from "./BlogCard";
import axios from "axios";
import "./blog.css";

import AddCircleIcon from '@mui/icons-material/AddCircle';
import ArticleEditor from "./ArticleEditor";
import { BouncingLoader } from "../../pure-components/Loader";
import ConfirmationPopup from "../../pure-components/ConfirmationPopup";
import { ErrorToast, SuccessToast } from "../../pure-components/CustomToast";
import { toast } from "react-toastify";
import Constants from "../../services/constants";
import CookieManager from "../../services/CookieManager";

const PAGE_SIZE = 10;

const dataURLToBlob = (dataUrl) => {
  const [header, base64Data] = dataUrl.split(",");
  const mimeMatch = header.match(/data:(.*?);base64/);
  const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
  const binary = atob(base64Data);
  const array = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    array[i] = binary.charCodeAt(i);
  }

  return new Blob([array], { type: mime });
};

const formatDateForInput = (value) => {
  const date = value instanceof Date ? value : new Date(value || Date.now());

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const emptyArticle = {
  id: null,
  slug: "",
  category: Constants.BLOG_CATEGORIES.ARTICLES,
  title: "",
  description: "",
  date: formatDateForInput(new Date()),
  readTime: "2 min read",
  image: "",
  color: "pink",
  featured: false,
  readText: [""],
  draft: false,
};

const BlogListing = () => {
  const [blogs, setBlogs] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const loaderRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [articleData, setArticleData] = useState(emptyArticle);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All");

  const profileData = CookieManager.getCookie("user-data")?.length ? JSON.parse(CookieManager.getCookie("user-data")) : null;

  const articleType = {
    ...(profileData?.userType === "SUPER_ADMIN" && {"DRAFT": "Draft"}),
    "ALL": "All",
    ...Constants.BLOG_CATEGORIES
  }

  const getCategoryFilter = (category) => {
    if(selectedCategory === "Draft") return { draft: true };
    if(selectedCategory === "All") return { draft: false };
    else return { category: selectedCategory, draft: false };
  }

  const loadBlogs = async (nextPage = 1, append = false) => {
    if (loading) return;

    setLoading(true);

    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || "";
      const url = `${baseUrl}api/blog/list?page=${nextPage}&limit=${PAGE_SIZE}`;


    const response = await axios({
        method: "post",
        url: url,
        headers: {
          "Content-Type": "application/json",
        },
        data: JSON.stringify({ page: nextPage, limit: PAGE_SIZE, ...getCategoryFilter() }),
      })

    //   console.log("Blog list response : ", response);
      if (!response.data.success) {
        throw new Error(`Error loading Blogs`);
      }

    //   const payload = await response.json();
      const items = Array.isArray(response?.data?.data)
        ? response?.data?.data : [];

      const totalCount = Number(
        response?.data?.pagination?.total ?? 0
      );

      setBlogs((current) => (append ? [...current, ...items] : items));
      setPage(nextPage);
      setHasMore(response?.data?.pagination?.hasNextPage ?? false);
    } catch (error) {
      console.error("Failed to load blogs:", error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBlogs(1, false);
  }, []);

  useEffect(() => {
    loadBlogs(1, false);
  }, [selectedCategory]);

  useEffect(() => {
    const target = loaderRef.current;

    if (!target || loading || !hasMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry?.isIntersecting) {
          loadBlogs(page + 1, true);
        }
      },
      {
        rootMargin: "300px",
      }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [page, loading, hasMore]);

  const featured = blogs[0];
  const remaining = blogs.slice(1);

  const saveBlog = async (blog = null) => {
    if (!blog) return;

    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || "";
      const url = blog._id ?`${baseUrl}api/blog/${blog._id}` : `${baseUrl}api/blog/create`;

      const formData = new FormData();

        formData.append("title", blog.title);
        formData.append("slug", blog.slug);
        formData.append("category", blog.category);
        formData.append("description", blog.description);
        formData.append("date", blog.date);
        formData.append("readTime", blog.readTime);
        formData.append("featured", blog.featured);
        formData.append("color", blog.color);
        formData.append("draft", blog.draft);

        if (blog.readText && Array.isArray(blog.readText)) {
        blog.readText.forEach((block, index) => {
            formData.append(`readText[${index}]`, block);
        });
        }

        if (blog.image && blog.image.startsWith("data:image/")) {
        const blob = dataURLToBlob(blog.image);
        formData.append("image", blob, "article-image.jpg");
        } else if (blog.image) {
        formData.append("imageUrl", blog.image);
        }

        const xAuth = CookieManager.getCookie("x-access-token");

    const response = await axios({
        method: "post",
        url: url,
        data: formData,
        headers: {
          "Content-Type": "application/json",
          "x-access-token": xAuth,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Expires': '0',
        },
      });

    //   console.log("Blog list response : ", response);
      if (!response.data.success) {
        throw new Error(`Error saving Blog`);
      }else{
        toast(({ closeToast }) => <SuccessToast  message={"Article saved successfully."}/>);
      }

    } catch (error) {
      console.error("Failed to save blog:", error);
      toast(({ closeToast }) => <ErrorToast  message={"Failed to save article. " + error.response?.data?.message}/>);
      setHasMore(false);
    } finally {
      setOpen(false);
      loadBlogs(1, false);
    }
  };

  const handleEdit = (data) => {
    setArticleData(data); 
    setOpen(true);
  }

  const handleDelete = async (data) => {
    if (!data || !data._id) return;
    setArticleData(data);
    setConfirmDelete(true);
  }

  const deleteBlog = async (blog = null) => {
    if (!blog) return;

    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || "";
      const url = `${baseUrl}api/blog/${blog._id}`;

      const xAuth = CookieManager.getCookie("x-access-token");
      const response = await axios({
        method: "delete",
        url: url,
        headers: {
          "Content-Type": "application/json",
          "x-access-token": xAuth,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Expires': '0',
        },
      });

    //   console.log("Blog list response : ", response);
      if (!response.data.success) {
        throw new Error(`Error deleting Blog`);
      }else{
        toast(({ closeToast }) => <SuccessToast  message={"Article deleted successfully."}/>);
      }

    } catch (error) {
      console.error("Failed to delete blog:", error);
      toast(({ closeToast }) => <ErrorToast  message={"Failed to delete article. " + error.response?.data?.message}/>);
    } finally {
      setConfirmDelete(false);
      setArticleData(emptyArticle);
      loadBlogs(1, false);
    }
  };
  return (
    <main className="blog-page" style={{ marginTop: "56px" }}>
      <div className="w-full flex">
        <span className="w-1/3">
          <h1 className="blog-title">Syncmama Blog</h1>
        </span>
        <span className="w-2/3 flex justify-end items-center">
          <div className="article-editor-tabs">

            {Object.keys(articleType).map(e => {
              return <button
                type="button"
                className={
                  selectedCategory === articleType[e]
                    ? "active"
                    : ""
                }
                onClick={() => setSelectedCategory(articleType[e])}
              >
                {articleType[e]}
              </button>
            })}
          </div>
        </span>
      </div>
      
      {blogs.length === 0 && !loading && (
        <div className="text-center py-10">
          <p className="text-gray-500">No Articles found for selected category.</p>
        </div>
      )}
      {featured && (
        <section className="featured-section">
          <BlogCard blog={featured} featured showActions={profileData?.userType === "SUPER_ADMIN"} handleEdit ={handleEdit} handleDelete={handleDelete} />
        </section>
      )}

      <section className="blog-grid">
        {remaining.map((blog) => (
          <BlogCard key={blog.id ?? blog._id ?? blog.slug} blog={blog} showActions={profileData?.userType === "SUPER_ADMIN"} handleEdit={handleEdit} handleDelete={handleDelete} />
        ))}
      </section>

      {profileData?.userType === "SUPER_ADMIN" && 
      <button
        onClick={() => {setArticleData(emptyArticle); setOpen(true);}}
        className="fixed bottom-16 right-1 bg-pink-500 text-white border-0 p-3 rounded-full shadow-lg hover:bg-pink-600 transition"
        title="Add New Blog"
      >
        <AddCircleIcon />
      </button>}

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-3 md:p-6"
          onClick={() => {setOpen(false); setShowConfirmClose(true)}}
          aria-modal="true"
          role="dialog"
        >
          <div
            className="relative w-full max-w-5xl max-h-[85vh] overflow-y-scroll rounded-2xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
            style={{ maxHeight: "70vh" }}
          >
            <div className="flex items-center justify-end border-b border-slate-200 px-4 py-3">
              <button
                type="button"
                onClick={() => {setOpen(false); setShowConfirmClose(true)}}
                className="border-0 bg-transparent text-2xl leading-none cursor-pointer text-slate-600 hover:text-slate-900"
                aria-label="Close article editor"
              >
                ×
              </button>
            </div>

            
              <ArticleEditor article={articleData} setArticleData={setArticleData} onCancel={() => setOpen(false)} onSave={(data) => saveBlog(data)} />
            
          </div>
        </div>
      )}
      {
        showConfirmClose &&
        <div className="fixed inset-0 z-99 flex items-center justify-center bg-black bg-opacity-50 p-3 md:p-6">
          <ConfirmationPopup
          title={"Save Article?"}
            yesAction={() => {saveBlog(articleData); setShowConfirmClose(false)}}
            cancelAction={() => {setOpen(false); setShowConfirmClose(false)}}
          />
        </div>
      }
      {
        confirmDelete &&
        <div className="fixed inset-0 z-99 flex items-center justify-center bg-black bg-opacity-50 p-3 md:p-6">
          <ConfirmationPopup
          title={"Delete Article?"}
            yesAction={() => {deleteBlog(articleData); setConfirmDelete(false)}}
            cancelAction={() => {setConfirmDelete(false)}}

          />
        </div>
      }
      <div ref={loaderRef} className="infinite-loader">
        {loading && <BouncingLoader />}

        {!loading && !hasMore && blogs.length > 0 && (
          <span>You've reached the end.</span>
        )}
      </div>
    </main>
  );
};

export default BlogListing;